import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import { BASE_PATH } from 'config';
import { refreshTokenRequest, refreshTokenRequestWithCookies } from './refreshToken';

export const API_URL = process.env.REACT_APP_API_URL;

/**
 * Query Client for caching and state management.
 */
export const queryClient = new QueryClient();

/**
 * Axios API Client instance.
 */
export const apiClient = axios.create({
    baseURL: API_URL,
    withCredentials: true // Ensures cookies (refresh token) are sent with requests
});

/**
 * Request queue to handle multiple requests during token refresh.
 */
type FailedRequest = {
    resolve: (token: string) => void;
    reject: (error: AxiosError) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

/**
 * Process queued requests after refresh completion.
 */
const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token ?? '');
        }
    });
    failedQueue = [];
};

/**
 * Attach Authorization header to requests if token exists.
 */
apiClient.interceptors.request.use(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (config: AxiosRequestConfig): AxiosRequestConfig => {
        const token = getAuthToken();
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

const getAuthToken = (): string | null => {
    return localStorage.getItem('access_token'); // Retrieve from localStorage or your context store
};

const setTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken); // Store token in localStorage
    localStorage.setItem('refresh_token', refreshToken); // Store refresh token in localStorage
};

const getRefreshToken = (): string | null => {
    return localStorage.getItem('refresh_token'); // Retrieve from localStorage
};

const getLoginPath = () => {
    const base = typeof BASE_PATH === 'string' && BASE_PATH && BASE_PATH !== '/' ? String(BASE_PATH).replace(/\/$/, '') : '';
    return `${base}/login`;
};

const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = getLoginPath();
};

/**
 * Axios response interceptor to handle 403 errors and refresh tokens.
 */
apiClient.interceptors.response.use(
    (response: AxiosResponse) => response, // If the response is successful, just pass it along
    async (error: AxiosError) => {
        // Casting the original request with custom _retry property
        const originalRequest = error.config as AxiosRequestConfig & {
            _retry?: boolean;
        };

        // If error response status is 401 and retry hasn't happened yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            const requestUrl = originalRequest.url ?? '';
            const isAuthMeOrRefresh = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/refresh');
            if (isAuthMeOrRefresh) {
                return Promise.reject(error);
            }
            if (isRefreshing) {
                // Add our request to the failed requests queue and retry after obtaining a new token
                return new Promise<string>((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (token && originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err)); // If there's an error, pass the promise rejection
            }

            // Flag this as a retried request
            originalRequest._retry = true;
            // Flag to indicate token refresh is happening
            isRefreshing = true;

            try {
                const legacyRefreshToken = getRefreshToken();
                if (legacyRefreshToken) {
                    const { data } = await refreshTokenRequest(legacyRefreshToken);
                    setTokens(data.access_token, data.refresh_token);
                    if (apiClient.defaults.headers.common) {
                        apiClient.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
                    }
                    processQueue(null, data.access_token);
                    return await apiClient(originalRequest);
                }
                await refreshTokenRequestWithCookies();
                processQueue(null, '');
                return await apiClient(originalRequest);
            } catch (err) {
                // Process remaining requests and reject them with the error
                processQueue(err as AxiosError, null);
                // Logout the user
                logout();
                // Reject the original promise with the error
                return await Promise.reject(err);
                // After attempting refresh
            } finally {
                // Reset the refreshing flag
                isRefreshing = false;
            }
        }

        // If the error wasn't due to token expiry, reject promise with error
        return Promise.reject(error);
    }
);
